import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Key, PencilSimple, ShieldCheck, Trash } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import {
  REAUTH_REQUIRED_CODE,
  type DeliverableKind,
  type RevealResult,
  type StaffDeliverable,
} from '@mybusinessmate/schema';
import {
  revealHistoryQuery,
  staffVaultQuery,
  useDeleteDeliverable,
  useRevealLogin,
  vaultFileUrl,
} from '../../lib/deliverables-api';
import { cx } from '../../lib/cx';
import { useSession } from '../../lib/session';
import { toasts } from '../../lib/toast-presets';
import {
  countdownText,
  matchesStaffSearch,
  revealedWhen,
  sharedWithClient,
  staffMeta,
  vaultIcon,
} from '../../lib/vault-display';
import { RowMenu, type RowMenuItem } from '../directory/row-menu';
import { Icon } from '../Icon';
import { useStaffReauth } from '../settings/use-staff-reauth';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { EmptyState } from '../ui/empty-state';
import { SegmentedControl } from '../ui/segmented-control';
import { SkeletonRows } from '../ui/skeleton';
import { ErrorState, NoResults } from '../ui/state-views';
import { StatusTag } from '../ui/status-tag';
import { textLinkClass } from '../ui/text-link';
import { useToast } from '../ui/toast';
import { FilterSelectPill, pillClass, ToolbarSearch } from '../ui/toolbar-controls';
import { VaultDrawer } from './vault-drawer';

type KindFilter = 'all' | DeliverableKind;
type Visibility = '' | 'client' | 'team';

const GROUPS: { kind: DeliverableKind; label: string }[] = [
  { kind: 'login', label: 'Logins' },
  { kind: 'file', label: 'Files' },
  { kind: 'link', label: 'Links' },
];

/** What the grey tile of a row says and what its button does: a login is opened, a file downloaded, a link copied. */
const ACTION: Record<DeliverableKind, string> = { login: 'Reveal', file: 'Download', link: 'Copy link' };

/** One value of an open login (Figma A05.11c): its name, the value, who can see it, and Copy. */
function RevealedField({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  /** "Client can see", "Client can see · secret", "Team only". */
  note: string;
}) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast(toasts.done('Copied'));
    } catch {
      toast(toasts.failed('Couldn’t copy', 'Select the text and copy it.'));
    }
  };
  return (
    <div className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-sm) bg-surface-card px-(--mbm-space-3-5) py-(--mbm-space-2-5)">
      <span className="w-(--mbm-layout-vault-field-label-width) shrink-0 truncate text-label-medium text-text-secondary">
        {label}
      </span>
      <span className="min-w-0 flex-1 select-all break-all font-mono text-body-small text-text-primary">{value}</span>
      <span className="shrink-0 text-body-xsmall text-text-tertiary">{note}</span>
      <button
        type="button"
        aria-label={`Copy ${label.toLowerCase()}`}
        onClick={() => void copy()}
        className={cx(textLinkClass, 'shrink-0')}
      >
        Copy
      </button>
    </div>
  );
}

/** The panel under an open login: its values, that the view is logged, and who has opened it before. */
function OpenPanel({
  item,
  result,
  secondsLeft,
  viewer,
}: {
  item: StaffDeliverable;
  result: RevealResult;
  secondsLeft: number;
  viewer: string;
}) {
  const history = useQuery(revealHistoryQuery(item.id));
  const [all, setAll] = useState(false);
  const flags = new Map(item.fields.map((f) => [f.id, f]));
  const entries = history.data?.items ?? [];
  const shown = all ? entries : entries.slice(0, 3);
  return (
    <div className="flex flex-col gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-option-tile) bg-surface-tint p-(--mbm-space-4)">
      {result.fields.map((f) => {
        const flag = flags.get(f.id);
        return (
          <RevealedField
            key={f.id}
            label={f.label}
            value={f.value}
            note={flag?.visibleToClient ? `Client can see${flag.secret ? ' · secret' : ''}` : 'Team only'}
          />
        );
      })}
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <p className="flex items-center gap-(--mbm-space-2) text-body-xsmall text-text-secondary">
          <Icon icon={ShieldCheck} size="sm" />
          This view is logged · {viewer}, just now · hides again in {countdownText(secondsLeft)}
        </p>
        {entries.length > 3 ? (
          <button type="button" onClick={() => setAll((v) => !v)} className={textLinkClass}>
            {all ? 'Fewer' : 'Reveal history'}
          </button>
        ) : null}
      </div>
      <p className="text-label-small uppercase text-text-tertiary">Recent reveals</p>
      {history.isLoading ? (
        <SkeletonRows rows={1} label="Loading who opened it" />
      ) : history.isError ? (
        <p className="text-body-xsmall text-text-tertiary">Couldn’t load who opened it.</p>
      ) : shown.length === 0 ? (
        <p className="text-body-xsmall text-text-tertiary">No one has opened it yet.</p>
      ) : (
        <ul className="flex flex-col gap-(--mbm-space-1-5)">
          {shown.map((entry) => (
            <li key={entry.id} className="text-body-xsmall text-text-secondary">
              {entry.actorName ?? 'Someone who has left'} ({entry.actorRole}) · {revealedWhen(entry.at)}
              {entry.thisDevice ? ' · this device' : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The business's vault as the team keeps it (Figma A05.11): everything shared with the client and everything kept for
 * the team, grouped as logins, files and links. Opening a login asks for the password again, shows it for 30 seconds
 * and is logged; secrets reach the browser only through that, never in the list.
 */
export function StaffVault({
  businessId,
  businessName,
  projects,
  canManage,
  canDelete,
  addRequest,
}: {
  businessId: string;
  businessName: string;
  projects: { id: string; name: string }[];
  canManage: boolean;
  canDelete: boolean;
  /** Bumped by the page's "Add item" button, which sits in the header above. */
  addRequest: number;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const { guard, dialog } = useStaffReauth();
  const vault = useQuery(staffVaultQuery(businessId));
  const reveal = useRevealLogin();
  const remove = useDeleteDeliverable(businessId);

  const [text, setText] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [project, setProject] = useState('');
  const [visible, setVisible] = useState<Visibility>('');
  const [open, setOpen] = useState<{ id: string; result: RevealResult; until: number } | null>(null);
  const [busyId, setBusyId] = useState<string>();
  const [now, setNow] = useState(() => Date.now());
  const [drawer, setDrawer] = useState<{ item: StaffDeliverable | null; key: number } | null>(null);
  const [deleting, setDeleting] = useState<StaffDeliverable | null>(null);

  // The header's "Add item" opens the drawer; each press is a fresh, empty form.
  useEffect(() => {
    if (addRequest > 0) setDrawer({ item: null, key: addRequest });
  }, [addRequest]);

  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  useEffect(() => {
    if (open && now >= open.until) setOpen(null);
  }, [now, open]);

  const items = useMemo(() => vault.data?.items ?? [], [vault.data]);
  const counts = {
    all: items.length,
    login: items.filter((i) => i.kind === 'login').length,
    file: items.filter((i) => i.kind === 'file').length,
    link: items.filter((i) => i.kind === 'link').length,
  };
  const filtered = items.filter(
    (i) =>
      (kind === 'all' || i.kind === kind) &&
      matchesStaffSearch(i, text) &&
      (!project || i.projectId === project) &&
      (visible === '' || (visible === 'client' ? sharedWithClient(i) : !sharedWithClient(i))),
  );
  const clear = () => {
    setText('');
    setKind('all');
    setProject('');
    setVisible('');
  };

  const show = (item: StaffDeliverable, always = false) =>
    guard(
      'reveal this login',
      () => {
        setBusyId(item.id);
        reveal.mutate(item.id, {
          onSuccess: (result) => {
            const start = Date.now();
            setNow(start);
            setOpen({ id: item.id, result, until: start + result.hideAfterSeconds * 1000 });
            // This view is now in the log: show it.
            void queryClient.invalidateQueries({ queryKey: revealHistoryQuery(item.id).queryKey });
          },
          onError: (err) => {
            const e = err as Partial<MappedApiError>;
            if (e.code === REAUTH_REQUIRED_CODE) return show(item, true);
            toast(
              e.status === 429
                ? toasts.rateLimited('Too many reveals', 'Wait a minute, then try again.')
                : toasts.failed('Couldn’t show this login', e.message ?? 'Please try again.'),
            );
          },
          onSettled: () => setBusyId(undefined),
        });
      },
      { always },
    );

  const act = async (item: StaffDeliverable) => {
    if (item.kind === 'login') {
      if (open?.id === item.id) setOpen(null);
      else show(item);
      return;
    }
    const field = item.fields[0];
    if (!field) return;
    try {
      if (item.kind === 'file') {
        const { url } = await vaultFileUrl(item.id, field.id);
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        await navigator.clipboard.writeText(field.value ?? '');
        toast(toasts.done('Link copied'));
      }
    } catch {
      toast(toasts.failed(item.kind === 'file' ? 'Couldn’t open the file' : 'Couldn’t copy the link'));
    }
  };

  const menuFor = (item: StaffDeliverable): RowMenuItem[] => [
    ...(canManage ? [{ label: 'Edit', icon: PencilSimple, onSelect: () => setDrawer({ item, key: Date.now() }) }] : []),
    ...(canDelete ? [{ label: 'Delete', icon: Trash, separated: canManage, onSelect: () => setDeleting(item) }] : []),
  ];

  const body = vault.isLoading ? (
    <SkeletonRows rows={5} label="Loading the vault" className="px-(--mbm-space-1) py-(--mbm-space-3)" />
  ) : vault.isError ? (
    <ErrorState title="Couldn’t load the vault" onRetry={() => void vault.refetch()} />
  ) : items.length === 0 ? (
    <EmptyState
      icon={Key}
      title="Nothing in the vault yet"
      description={`Add the logins, files and links ${businessName} hands over. Anything marked “Client can see” shows up in their app.`}
      action={
        canManage ? (
          <button
            type="button"
            onClick={() => setDrawer({ item: null, key: Date.now() })}
            className={pillClass('dark', 'lg')}
          >
            Add item
          </button>
        ) : undefined
      }
    />
  ) : filtered.length === 0 ? (
    <NoResults query={text.trim() || undefined} onClear={clear} />
  ) : (
    GROUPS.map(({ kind: group, label }) => {
      const rows = filtered.filter((i) => i.kind === group);
      if (rows.length === 0) return null;
      return (
        <section key={group} aria-label={label} className="flex flex-col">
          <h3 className="pb-(--mbm-space-1) pt-(--mbm-space-3) text-label-small uppercase text-text-tertiary">
            {label} · {rows.length}
          </h3>
          <ul className="flex flex-col">
            {rows.map((item) => {
              const shared = sharedWithClient(item);
              const isOpen = open?.id === item.id;
              return (
                <li key={item.id} className="flex flex-col gap-(--mbm-space-2-5)">
                  <div className="flex items-center gap-(--mbm-space-3-5) py-(--mbm-space-3)">
                    <span className="flex size-(--mbm-size-icon-tile-project) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-action-primary text-text-on-dark">
                      <Icon
                        icon={
                          item.kind === 'file'
                            ? vaultIcon({ icon: null, contentType: item.fields[0]?.file?.contentType }, 'file')
                            : vaultIcon(item, item.kind)
                        }
                        size="lg"
                      />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
                      <span className="truncate text-label-medium text-text-primary">{item.title}</span>
                      <span className="truncate text-body-xsmall text-text-tertiary">{staffMeta(item)}</span>
                    </span>
                    <StatusTag
                      label={shared ? 'CLIENT CAN SEE' : 'TEAM ONLY'}
                      tone={shared ? 'green' : 'grey'}
                      className="shrink-0 self-center"
                    />
                    <button
                      type="button"
                      disabled={busyId === item.id}
                      aria-label={isOpen ? `Hide ${item.title}` : `${ACTION[item.kind]} ${item.title}`}
                      onClick={() => void act(item)}
                      className={cx(pillClass('light', 'lg'), 'min-w-(--mbm-layout-vault-action-width) justify-center')}
                    >
                      {isOpen ? 'Hide' : ACTION[item.kind]}
                    </button>
                    {menuFor(item).length > 0 ? (
                      <RowMenu label={`More for ${item.title}`} items={menuFor(item)} />
                    ) : (
                      <span className="size-(--mbm-space-7)" />
                    )}
                  </div>
                  {isOpen && open ? (
                    <OpenPanel
                      item={item}
                      result={open.result}
                      secondsLeft={(open.until - now) / 1000}
                      viewer={session?.user.name ?? 'You'}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      );
    })
  );

  return (
    <div className="flex flex-col gap-(--mbm-space-4)">
      <div className="flex flex-wrap items-center gap-(--mbm-space-2-5)">
        <ToolbarSearch
          filled
          size="lg"
          aria-label="Search the vault"
          placeholder="Search the vault"
          value={text}
          onChange={setText}
          className="w-full sm:w-(--mbm-layout-toolbar-search-narrow-width)"
        />
        <SegmentedControl
          shape="pill"
          size="lg"
          options={[
            { value: 'all', label: 'All', count: counts.all },
            { value: 'login', label: 'Logins', count: counts.login },
            { value: 'file', label: 'Files', count: counts.file },
            { value: 'link', label: 'Links', count: counts.link },
          ]}
          value={kind}
          onChange={setKind}
        />
        <span className="ml-auto flex flex-wrap items-center gap-(--mbm-space-2-5)">
          <FilterSelectPill
            label="Project"
            size="lg"
            showAll
            value={project}
            onChange={setProject}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
          <FilterSelectPill
            label="Visible to"
            size="lg"
            showAll
            value={visible}
            onChange={(v) => setVisible(v as Visibility)}
            options={[
              { value: 'client', label: 'Client can see' },
              { value: 'team', label: 'Team only' },
            ]}
          />
        </span>
      </div>

      <section className="flex flex-col gap-(--mbm-space-1) rounded-(--mbm-component-radius-card) bg-surface-card px-(--mbm-space-5-5) py-(--mbm-space-5) shadow-(--mbm-shadow-table-card)">
        <div className="flex flex-col gap-(--mbm-space-0-5)">
          <h2 className="text-heading-h4 text-text-primary">Deliverables vault</h2>
          <p className="text-body-xsmall text-text-tertiary">
            Logins, files and links you hand over to {businessName}. Clients only see items marked ‘Client can see’.
            Every reveal is logged.
          </p>
        </div>
        {body}
      </section>

      {drawer ? (
        <VaultDrawer
          key={drawer.key}
          open
          item={drawer.item}
          businessId={businessId}
          businessName={businessName}
          projects={projects}
          onClose={() => setDrawer(null)}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete “${deleting?.title ?? ''}”?`}
        description="The client stops seeing it right away, and anything stored with it is removed. This can’t be undone."
        requireText={deleting?.title}
        requireLabel="name"
        confirmLabel="Delete item"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const target = deleting;
          if (!target) return;
          remove.mutate(target.id, {
            onSuccess: () => {
              if (open?.id === target.id) setOpen(null);
              setDeleting(null);
              toast(toasts.done('Deleted', `“${target.title}” is gone from the vault.`));
            },
            onError: () => toast(toasts.failed('Couldn’t delete it', 'Something went wrong on our side. Try again.')),
          });
        }}
      />
      {dialog}
    </div>
  );
}
